// src/services/api.js
import axios from 'axios';
import M3UParser from './m3uParser';
import DBService from './db';
import StorageService from '../utils/storage';
import proxyService from './proxyService';

const API_CONFIG = {
  apiKey: '1234567890', // Exemplo de chave de API
  baseUrl: 'https://myiptvapi.com/api', // Exemplo de URL base
  userId: '56568605',
  userPassword: '89231',
  streamUrl: 'http://pfsv.io:80/${userId}/${userPassword}', // URL original
  epgUrl: 'http://epg.pfsv.io',
  m3uUrl: 'http://pfsv.io/get.php?username=56568605&password=89231&output=ts&type=m3u_plus',
  // Diferentes URLs de streaming disponíveis
  streamUrls: {
    ts: 'http://bsrv.io/56568605/89231',    // URL/M3U TS|HLS
    mpegts: 'http://bsrv.cc/56568605/89231', // URL/M3U MPEGTS
    smart: 'http://bsweb.me/smart.php?u=56568605&p=89231' // URL/M3U Smart
  }
};

// Tipos de servidores
const serverTypes = [
  'original', // Usa a URL original do arquivo M3U
  'cdn018',   // Usa cdn018.io
  'pfsv'      // Usa pfsv.io
];

// Chaves para o localStorage
const STORAGE_KEYS = {
  GROUPS: 'iptv_groups',
  LAST_UPDATE: 'iptv_last_update'
};

// Tempo de expiração do cache em milissegundos (24 horas)
const CACHE_EXPIRATION = 24 * 60 * 60 * 1000;

// Eventos de progresso
const PROGRESS_EVENTS = {
  DOWNLOAD_START: 'downloadStart',
  DOWNLOAD_PROGRESS: 'downloadProgress',
  PROCESSING_START: 'processingStart',
  PROCESSING_PROGRESS: 'processingProgress',
  PROCESSING_COMPLETE: 'processingComplete',
  ERROR: 'error'
};

class ApiService {
  constructor() {
    this.parser = new M3UParser();
    this.channels = [];
    this.groups = new Set();
    this.epgData = null;
    this.isLoaded = false;
    this.currentServer = 0;
    this.loadedChunks = new Set();
    this.progressListeners = new Set();
    this.totalChannels = 0;
    this.processedChannels = 0;
  }

  // Adiciona um listener para eventos de progresso
  addProgressListener(listener) {
    this.progressListeners.add(listener);
  }

  // Remove um listener
  removeProgressListener(listener) {
    this.progressListeners.delete(listener);
  }

  // Emite um evento de progresso
  emitProgress(event, data) {
    this.progressListeners.forEach(listener => {
      listener({ event, data });
    });
  }

  // Salva um chunk de canais no IndexedDB
  async saveChannelChunk(chunkIndex, channels) {
    try {
      const essentialChannels = channels.map(channel => ({
        id: channel.id,
        name: channel.name,
        cleanName: channel.cleanName || channel.name,
        group: channel.group,
        url: channel.url,
        logo: channel.logo,
        quality: channel.quality
      }));
      await DBService.saveChannelChunk(chunkIndex, essentialChannels);
      this.loadedChunks.add(chunkIndex);
    } catch (error) {
      console.error(`Erro ao salvar chunk ${chunkIndex}:`, error);
    }
  }

  // Carrega um chunk específico do IndexedDB
  async loadChannelChunk(chunkIndex) {
    try {
      const channels = await DBService.loadChannelChunk(chunkIndex);
      return channels;
    } catch (error) {
      console.error(`Erro ao carregar chunk ${chunkIndex}:`, error);
      return null;
    }
  }

  // Verifica se o cache está válido
  isCacheValid() {
    try {
      const lastUpdate = localStorage.getItem(STORAGE_KEYS.LAST_UPDATE);
      if (!lastUpdate) return false;

      const updateTime = parseInt(lastUpdate, 10);
      const now = Date.now();
      return (now - updateTime) <= CACHE_EXPIRATION;
    } catch (error) {
      console.error('Erro ao verificar validade do cache:', error);
      return false;
    }
  }

  // Organiza os canais por qualidade e nome
  organizeChannelsByQuality(channels) {
    const channelMap = {};

    channels.forEach(channel => {
      // Usa o nome limpo como chave para agrupar canais do mesmo tipo
      const cleanName = channel.cleanName || this.parser.cleanChannelName(channel.name);

      if (!channelMap[cleanName]) {
        channelMap[cleanName] = {
          cleanName,
          group: channel.group,
          logo: channel.logo,
          versions: []
        };
      }

      // Adiciona esta versão do canal
      channelMap[cleanName].versions.push({
        id: channel.id,
        name: channel.name,
        url: channel.url,
        quality: channel.quality || this.parser.detectQuality(channel.name)
      });
    });

    // Para cada canal, ordena as versões por qualidade (4K > FHD > HD > SD)
    Object.values(channelMap).forEach(channelGroup => {
      channelGroup.versions.sort((a, b) => {
        const qualityOrder = { '4K': 4, 'FHD': 3, 'HD': 2, 'SD': 1 };
        return (qualityOrder[b.quality] || 0) - (qualityOrder[a.quality] || 0);
      });
    });

    return channelMap;
  }

  // Seleciona a versão do canal com base nas configurações do usuário
  selectChannelVersionByUserPreference(channelGroup) {
    const settings = StorageService.getSettings();
    const preferredQuality = settings.playerQuality;

    // Se a preferência for automática, retorna a melhor qualidade disponível
    if (preferredQuality === 'auto') {
      return channelGroup.versions[0]; // Já está ordenado por qualidade
    }

    // Mapeamento de configurações para qualidades
    const qualityMap = {
      'hd': ['HD', 'FHD', '4K'],
      'sd': ['SD'],
      'low': ['SD']
    };

    // Procura por uma versão que corresponda à preferência do usuário
    const preferredQualities = qualityMap[preferredQuality] || ['HD'];

    // Tenta encontrar uma versão com a qualidade preferida
    for (const quality of preferredQualities) {
      const version = channelGroup.versions.find(v => v.quality === quality);
      if (version) return version;
    }

    // Se não encontrar, retorna a primeira versão disponível
    return channelGroup.versions[0];
  }

  // Carrega dados do banco de dados local
  async loadFromDatabase() {
    try {
      const storedData = await DBService.loadOrganizedChannels();

      if (!storedData || !storedData.organizedChannels) {
        return null;
      }

      const organizedChannels = storedData.organizedChannels;
      this.groups = storedData.groups || new Set();

      // Converter estrutura organizada de volta para array de canais selecionados
      const selectedChannels = [];
      Object.values(organizedChannels).forEach(channelGroup => {
        const selectedVersion = this.selectChannelVersionByUserPreference(channelGroup);
        selectedChannels.push({
          id: selectedVersion.id,
          name: selectedVersion.name,
          cleanName: channelGroup.cleanName,
          group: channelGroup.group,
          logo: channelGroup.logo,
          url: selectedVersion.url,
          quality: selectedVersion.quality
        });
      });

      this.channels = selectedChannels;
      this.isLoaded = true;

      console.log(`Carregados ${this.channels.length} canais do banco de dados local`);

      return { channels: this.channels, groups: this.groups };
    } catch (error) {
      console.error('Erro ao carregar do banco de dados:', error);
      return null;
    }
  }

  async loadChannels(forceRefresh = false) {
    try {
      // Se já está carregado e não é refresh forçado, retorna dados em memória
      if (this.isLoaded && !forceRefresh) {
        return { channels: this.channels, groups: this.groups };
      }

      // Se não é refresh forçado, verifica se existe dados no IndexedDB
      if (!forceRefresh) {
        const hasData = await DBService.hasStoredData();

        if (hasData) {
          console.log('Carregando canais do banco de dados local...');
          const dbResult = await this.loadFromDatabase();

          if (dbResult) {
            return dbResult;
          }

          console.log('Falha ao carregar do banco, fazendo download...');
        } else {
          console.log('Nenhum dado encontrado no banco, fazendo download inicial...');
        }
      } else {
        console.log('Atualização forçada, fazendo download...');
      }

      // Se chegou aqui, precisa fazer download e processamento
      this.emitProgress(PROGRESS_EVENTS.DOWNLOAD_START);
      console.log('Baixando e processando lista de canais...');

      let content;

      if (proxyService.shouldUseProxy(API_CONFIG.m3uUrl)) {
        console.log('Usando proxy para carregar M3U devido ao protocolo HTTPS');
        content = await proxyService.getM3UContent(API_CONFIG.m3uUrl);

        this.emitProgress(PROGRESS_EVENTS.DOWNLOAD_PROGRESS, { percent: 100 });
      } else {
        const response = await axios.get(API_CONFIG.m3uUrl, {
          onDownloadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / (progressEvent.total || progressEvent.loaded));
            this.emitProgress(PROGRESS_EVENTS.DOWNLOAD_PROGRESS, { percent: percentCompleted });
          }
        });
        content = response.data;
      }

      // Limpar dados antigos
      await DBService.clearAllChunks();
      await DBService.clearOrganizedChannels();

      this.emitProgress(PROGRESS_EVENTS.PROCESSING_START);

      const parseResult = this.parser.parse(content);
      const allChannels = parseResult.channels;
      this.groups = new Set(parseResult.groups);

      // Organizar canais por qualidade
      const organizedChannels = this.organizeChannelsByQuality(allChannels);

      // Salvar estrutura completa organizada no IndexedDB
      await DBService.saveOrganizedChannels(organizedChannels, this.groups);
      console.log('Estrutura completa de canais salva no IndexedDB');

      // Selecionar versões dos canais baseado nas preferências do usuário
      const selectedChannels = [];
      Object.values(organizedChannels).forEach(channelGroup => {
        const selectedVersion = this.selectChannelVersionByUserPreference(channelGroup);
        selectedChannels.push({
          id: selectedVersion.id,
          name: selectedVersion.name,
          cleanName: channelGroup.cleanName,
          group: channelGroup.group,
          logo: channelGroup.logo,
          url: selectedVersion.url,
          quality: selectedVersion.quality
        });
      });

      this.totalChannels = selectedChannels.length;

      // Manter compatibilidade com sistema de chunks (para transição)
      const ADJUSTED_CHUNK_SIZE = 200;
      const totalChunks = Math.ceil(selectedChannels.length / ADJUSTED_CHUNK_SIZE);

      for (let i = 0; i < totalChunks; i++) {
        const startIndex = i * ADJUSTED_CHUNK_SIZE;
        const endIndex = Math.min(startIndex + ADJUSTED_CHUNK_SIZE, selectedChannels.length);
        const chunk = selectedChannels.slice(startIndex, endIndex);

        await this.saveChannelChunk(i, chunk);

        this.processedChannels = endIndex;
        const percent = Math.round((this.processedChannels * 100) / this.totalChannels);
        this.emitProgress(PROGRESS_EVENTS.PROCESSING_PROGRESS, {
          percent,
          currentChunk: i,
          totalChannels: this.processedChannels
        });
      }

      this.emitProgress(PROGRESS_EVENTS.PROCESSING_COMPLETE, {
        totalChunks,
        totalChannels: this.totalChannels
      });

      // Salvar timestamp de última atualização
      localStorage.setItem(STORAGE_KEYS.LAST_UPDATE, Date.now().toString());
      localStorage.setItem(STORAGE_KEYS.GROUPS, JSON.stringify(Array.from(this.groups)));

      // Carregar canais selecionados em memória
      this.channels = selectedChannels;

      console.log(`Total de canais carregados em memória: ${this.channels.length}`);
      this.isLoaded = true;

      return { channels: this.channels, groups: this.groups };

    } catch (error) {
      console.error('Erro ao carregar canais:', error);
      this.emitProgress(PROGRESS_EVENTS.ERROR, { error });

      // Tentar carregar do banco como fallback
      const dbResult = await this.loadFromDatabase();
      if (dbResult) {
        console.log('Usando dados do banco como fallback após erro');
        return dbResult;
      }

      throw error;
    }
  }

  // Carrega mais canais sob demanda
  async loadMoreChannels(chunkIndex) {
    if (this.loadedChunks.has(chunkIndex)) {
      return [];
    }

    const chunk = await this.loadChannelChunk(chunkIndex);
    if (chunk) {
      this.channels = [...this.channels, ...chunk];
      return chunk;
    }

    return [];
  }

  async loadEPG() {
    try {
      let epgData;

      if (proxyService.shouldUseProxy(API_CONFIG.epgUrl)) {
        console.log('Usando proxy para carregar EPG devido ao protocolo HTTPS');
        epgData = await proxyService.getEPGContent(API_CONFIG.epgUrl);
      } else {
        const response = await axios.get(API_CONFIG.epgUrl);
        epgData = response.data;
      }

      this.epgData = epgData;
      return this.epgData;
    } catch (error) {
      console.error('Erro ao carregar EPG:', error);
      throw error;
    }
  }

  getChannelsByGroup(group) {
    return this.channels.filter(channel => channel.group === group);
  }

  getChannel(channelId) {
    return this.channels.find(channel => channel.id === channelId);
  }

  /**
   * Obtém uma URL de stream com base no canal, servidor e formato
   * @param {Object} channel - Canal para obter a URL do stream
   * @param {boolean} addToken - Se deve adicionar um token aleatório para evitar cache
   * @param {number} serverIndex - Índice do servidor a usar (opcional)
   * @param {string} format - Formato do stream (ts, m3u8, mp4)
   * @returns {string} URL do stream
   */
  getStreamUrl(channel, addToken = false, serverIndex = 0, format = '') {
    if (!channel) return '';

    const originalUrl = channel.url;

    let channelId = '';

    try {
      if (originalUrl && originalUrl.includes('/')) {
        const urlParts = originalUrl.split('/');
        const lastPart = urlParts[urlParts.length - 1];
        channelId = lastPart.split('.')[0];
        console.log(`ID extraído da URL original: ${channelId}`);
      } else {
        channelId = channel.id;
        console.log(`Usando ID do objeto channel: ${channelId}`);
      }
    } catch (error) {
      console.error('Erro ao extrair ID da URL:', error);
      channelId = channel.id;
    }

    const serverType = this.getServerTypes()[serverIndex] || 'original';

    let streamUrl = '';

    switch (serverType) {
      case 'cdn018':
        streamUrl = `http://cdn018.io:80/56568605/89231/${channelId}.ts`;
        console.log(`Usando URL cdn018: ${streamUrl}`);
        break;
      case 'pfsv':
        streamUrl = `http://pfsv.io:80/56568605/89231/${channelId}.ts`;
        console.log(`Usando URL pfsv: ${streamUrl}`);
        break;
      case 'original':
      default:
        streamUrl = originalUrl;
        console.log(`Usando URL original: ${streamUrl}`);
        break;
    }

    if (proxyService.shouldUseProxy(streamUrl)) {
      console.log('Aplicando proxy para URL de stream devido ao protocolo HTTPS');
      streamUrl = proxyService.getProxiedStreamUrl(streamUrl);
    }

    if (addToken) {
      const token = Math.random().toString(36).substring(2, 10);
      const separator = streamUrl.includes('?') ? '&' : '?';
      return `${streamUrl}${separator}token=${token}`;
    }

    return streamUrl;
  }

  // Avança para o próximo tipo de servidor
  nextServer() {
    this.currentServer = (this.currentServer + 1) % this.getServerTypes().length;
    console.log(`Mudando para servidor: ${this.getServerTypes()[this.currentServer]} (índice ${this.currentServer})`);
    return this.currentServer;
  }

  // Reseta para o servidor padrão (original)
  resetServer() {
    this.currentServer = 0;
    console.log(`Resetando para servidor padrão: ${this.getServerTypes()[0]}`);
    return this.currentServer;
  }

  /**
   * Retorna os tipos de servidores disponíveis
   * @returns {string[]} Lista de tipos de servidores
   */
  getServerTypes() {
    return serverTypes;
  }

  searchChannels(query) {
    return this.parser.searchChannels(query);
  }

  getEPGForChannel(channelId, date = new Date()) {
    // Implementação para buscar EPG de um canal específico
    if (!this.epgData) return [];

    // Retorna programação vazia até implementar o parser EPG
    return [];
  }

  // Método para forçar a atualização da lista de canais
  async refreshChannelList() {
    // Limpar dados antigos antes de atualizar
    await DBService.clearOrganizedChannels();
    await DBService.clearAllChunks();

    // Forçar atualização completa
    return this.loadChannels(true);
  }
}

const apiService = new ApiService();
export default apiService;